/**
 * Wave 2 — /membership interstitial.
 *
 * Cinematic, documentary-toned bridge between TCP's public surface and
 * the Shopify checkout. The emotional goal: entering deeper archive
 * access — never being dumped into ecommerce.
 *
 * Replaces every external "Become a member" link in the app. Routes
 * out to Shopify ONLY after the user explicitly chooses a plan.
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "../components/Header";
import LogoMark from "../components/LogoMark";
import { ArrowRight, ArrowUpRight, Lock, FileSearch, Network, Compass, Clock, Archive } from "lucide-react";

const SHOPIFY_BASE = process.env.REACT_APP_MEMBERSHIP_URL || "/membership";
const PUBLIC_RETURN = "/activate-account";

// Final deep-link checkout URLs — Shopify cart permalinks that skip the
// product page entirely. Variant + selling-plan IDs configured Feb 2026.
const CHECKOUT_URLS = {
    monthly: process.env.REACT_APP_CHECKOUT_URL_MONTHLY
        || "https://thecontextproject.com/cart/53807610003795:1?selling_plan=691040354643",
    yearly: process.env.REACT_APP_CHECKOUT_URL_YEARLY
        || "https://thecontextproject.com/cart/53807607480659:1?selling_plan=691040387411",
    lifetime: process.env.REACT_APP_CHECKOUT_URL_LIFETIME
        || "https://thecontextproject.com/cart/53807608758611:1",
};

function planLink(planId) {
    // Deep-link straight into Shopify checkout. The Order Status page
    // redirect script (configured in Shopify Admin) brings the user back
    // to library.thecontextproject.com/activate-account?email={{customer.email}}
    // after payment.
    return CHECKOUT_URLS[planId] || SHOPIFY_BASE;
}

const WHATS_INSIDE = [
    {
        icon: FileSearch,
        title: "Full source chronologies",
        body: "Every cited document with publisher, funding label, and verification date — including ~85% of the archive currently held behind the gate.",
    },
    {
        icon: Network,
        title: "Cross-entity relationship maps",
        body: "Co-occurring entities across investigations, ownership chains, and documented connections through shared sources or events.",
    },
    {
        icon: Clock,
        title: "Complete documented timelines",
        body: "129+ documented events across the library — full descriptions, source attributions, and decade-level navigation.",
    },
    {
        icon: Compass,
        title: "Investigation pathways",
        body: "Curated next-thread recommendations on every topic — the editorial map for following an investigation through the archive.",
    },
    {
        icon: Archive,
        title: "Future Outlook · Unknowns · Real-world Examples",
        body: "Premium sections inside every investigation — what's still contested, what's likely next, what's already happening.",
    },
    {
        icon: Lock,
        title: "Member-only investigations",
        body: "Foundational hubs and entity profiles released first to members — full chronology, full source archive, full graph.",
    },
];

const PLANS = [
    {
        id: "monthly",
        eyebrow: "Monthly",
        price: "£17",
        cadence: "per month",
        line: "Cancel any time.",
        // Cinematic launch invitation — surfaced as a quiet detail row,
        // not as flashy promo. User enters code manually at checkout.
        intro: { headline: "First month £5", code: "TCP-LAUNCH" },
        primary: false,
    },
    {
        id: "yearly",
        eyebrow: "Yearly",
        price: "£177",
        cadence: "per year",
        line: "The investigative archive at calm pacing.",
        primary: true,
    },
    {
        id: "lifetime",
        eyebrow: "Lifetime",
        price: "£377",
        cadence: "one time",
        line: "Permanent access. Every future investigation included.",
        primary: false,
    },
];

export default function MembershipPage() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    // Where to return after Shopify checkout — defaults to /activate-account.
    // Captured server-side via Order Status redirect script.
    const ret = params.get("next") || PUBLIC_RETURN;

    return (
        <div className="min-h-screen bg-[#0A0F1A] text-white">
            <Header />
            <main className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-24" data-testid="membership-page">
                {/* Hero */}
                <section className="mb-20 sm:mb-24">
                    <div className="mb-10">
                        <LogoMark size="md" eyebrow="Members archive" subline="Continue the investigation" dataTestId="membership-logo" />
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6 max-w-3xl">
                        The deeper layer of every investigation.
                    </h1>
                    <p className="text-[15px] sm:text-[17px] text-[#94A3B8] leading-relaxed max-w-2xl">
                        The public surface is the introduction. The archive is the work. Full source chronologies, cross-entity maps, complete timelines, and investigation pathways — open to members.
                    </p>
                </section>

                {/* What unlocks */}
                <section className="mb-20 sm:mb-24">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-6">
                        Inside the archive
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
                        {WHATS_INSIDE.map((it) => {
                            const Icon = it.icon;
                            return (
                                <div key={it.title} className="flex items-start gap-4" data-testid={`membership-unlock-${it.title.split(" ")[0].toLowerCase()}`}>
                                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#56CCF2]/10 border border-[#56CCF2]/20 flex items-center justify-center text-[#56CCF2]">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <h3 className="text-[15px] font-semibold text-white mb-1.5">{it.title}</h3>
                                        <p className="text-[13.5px] text-[#94A3B8] leading-relaxed">{it.body}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Plans — refined cinematic restraint */}
                <section className="mb-20">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-10">
                        Choose your access
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
                        {PLANS.map((p) => (
                            <a
                                key={p.id}
                                href={planLink(p.id)}
                                data-testid={`membership-plan-${p.id}`}
                                className={`group relative block rounded-xl px-8 py-10 sm:px-9 sm:py-12 transition-colors ${
                                    p.primary
                                        ? "bg-[#0E1726] border border-[#56CCF2]/35 hover:border-[#56CCF2]/65"
                                        : "bg-transparent border border-[#1A2233] hover:border-[#56CCF2]/30"
                                }`}
                            >
                                {/* Subtle top accent on the recommended card — no SaaS badge */}
                                {p.primary ? (
                                    <span
                                        aria-hidden="true"
                                        className="absolute top-0 left-10 right-10 h-px bg-gradient-to-r from-transparent via-[#56CCF2]/60 to-transparent"
                                    />
                                ) : null}

                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] font-medium mb-10">
                                    {p.eyebrow}
                                </div>

                                {/* Dominant price */}
                                <div className="mb-1.5">
                                    <span className="text-[56px] sm:text-[64px] font-bold text-white tracking-tight leading-none">
                                        {p.price}
                                    </span>
                                </div>
                                <div className="text-[12px] text-[#64748B] tracking-wide mb-8">
                                    {p.cadence}
                                </div>

                                <p className="text-[13.5px] text-[#94A3B8] leading-relaxed">
                                    {p.line}
                                </p>
                                {p.intro ? (
                                    <p
                                        data-testid={`membership-plan-${p.id}-intro`}
                                        className="text-[12.5px] text-[#94A3B8]/75 leading-relaxed mt-2"
                                    >
                                        {p.intro.headline} with code{" "}
                                        <span
                                            data-testid={`membership-plan-${p.id}-code`}
                                            className="font-mono tracking-wider text-[#56CCF2] text-[12px] select-all"
                                        >
                                            {p.intro.code}
                                        </span>
                                    </p>
                                ) : null}

                                <div className="mt-12 pt-6 border-t border-[#1A2233]">
                                    <span className="inline-flex items-center gap-2 text-[13px] text-[#94A3B8] group-hover:text-[#56CCF2] transition-colors">
                                        Become a member
                                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </div>
                            </a>
                        ))}
                    </div>
                    <p className="text-[12px] text-[#64748B] mt-8 max-w-2xl leading-relaxed">
                        Billing handled by Shopify · secure checkout · cancel any time · every future investigation included.
                    </p>
                </section>

                {/* Quiet bottom rail — already a member */}
                <section className="border-t border-[#243044] pt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
                    <span className="text-[13px] text-[#94A3B8]">Already a member?</span>
                    <Link
                        to={`/account/login?next=${encodeURIComponent(ret)}`}
                        data-testid="membership-signin"
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#56CCF2] hover:text-white"
                    >
                        Sign in to continue <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <Link
                        to="/"
                        className="ml-auto inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.2em] text-[#64748B] hover:text-[#94A3B8]"
                    >
                        Back to the library
                    </Link>
                </section>
            </main>
        </div>
    );
}
